import { AbstractControl, FormGroup, NgForm } from '@angular/forms';

/**
 * Marks a single control as dirty/touched and re-runs its validation, so that
 * any existing validation output (e.g. the standard "required" message) becomes
 * visible in the surrounding UI, which typically only displays errors once the
 * control is dirty.
 *
 * @param control the control whose validation should be triggered and surfaced
 */
export function triggerControlValidation( control: AbstractControl ): void {
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
}

/**
 * 
 * @param form triggers form validation fo all controls without waiting for pending controls (i.e. for async validators)
 */
export function triggerFormValidation( form: NgForm | FormGroup ): void {
    Object.keys( form.controls ).forEach( control => form.controls[control].markAsDirty() );
}

/**
 * triggers validation of form and waits for pending controls
 * @param form 
 * @param checkIntervalInMs (default = 250) interval to check the pending state of the form controls
 * @param maxRetries (default = 12) maximum number of retries to check the pending state of the form controls. Interval is multiplied by the retry number for each subsequent try
 * @returns true, if all form controls complete pending state within the waiting time (default = 19.5s), false otherwise
 */
export async function triggerFormValidationAsync( form: NgForm | FormGroup, checkIntervalInMs = 250, maxRetries = 12 ): Promise<boolean> {
    triggerFormValidation( form );
    return until( () => !form.pending, checkIntervalInMs, maxRetries );
}

function until( conditionFunction: () => boolean, checkIntervalInMs: number, maxRetries: number ): Promise<boolean> {
    let currentTry = 0;

    const poll = ( resolve: ( value: any ) => any ) => {
        if ( conditionFunction() ) {
            resolve( true );
        }
        else if ( currentTry >= maxRetries ) {
            resolve( false );
        }
        else {
            currentTry++;
            setTimeout( ( _: any ) => poll( resolve ), checkIntervalInMs * ( currentTry + 1 ) );
        }
    };

    return new Promise<boolean>( poll );
}